import { Plus } from 'lucide-react';

import { TEAM_EVENT_CALENDAR_COLOR_ID } from '@lemoment/shared';

import { useTeamEventsToday } from '../hooks';
import { colorForId } from './colors';

const EVENT_COLOR = colorForId(TEAM_EVENT_CALENDAR_COLOR_ID);

interface TodayEventListProps {
  onAdd: () => void;
}

export const TodayEventList = ({ onAdd }: TodayEventListProps) => {
  const { data, isLoading, isError } = useTeamEventsToday();
  const items = data?.items ?? [];

  return (
    <section className="lift rounded-2xl border border-border bg-card/80 p-5">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-base font-bold tracking-tight text-foreground">
          오늘 팀 이벤트
        </h2>
        <div className="flex items-baseline gap-2">
          {!isLoading && !isError && items.length > 0 && (
            <span className="font-numeral text-2xl font-bold leading-none text-lemon-ink dark:text-lemon">
              {items.length}
              <span className="ml-0.5 text-xs font-medium text-muted-foreground">
                건
              </span>
            </span>
          )}
          <button
            type="button"
            onClick={onAdd}
            aria-label="팀 이벤트 추가"
            className="flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Plus className="h-4 w-4" strokeWidth={2.4} />
          </button>
        </div>
      </div>

      <div className="rule-citrus my-4" />

      {isLoading && (
        <div role="status" className="flex flex-col gap-3.5">
          <span className="sr-only">불러오는 중</span>
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-3" aria-hidden>
              <span className="skeleton h-2.5 w-2.5 rounded-sm" />
              <span className="skeleton h-4 w-24 rounded" />
              <span className="skeleton ml-auto h-4 w-12 rounded" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && isError && (
        <p className="text-sm text-destructive">불러오지 못했습니다</p>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          오늘 예정된 팀 이벤트가 없습니다.
        </p>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <ul className="flex flex-col gap-3.5">
          {items.map(({ event }) => (
            <li
              key={event.id}
              className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-accent"
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] ring-2 ring-background"
                style={{ backgroundColor: EVENT_COLOR }}
                aria-hidden
              />
              <span className="truncate font-medium text-foreground">
                {event.title}
              </span>
              <span className="ml-auto shrink-0 font-numeral text-sm text-muted-foreground">
                {event.allDay ? '종일' : `${event.startTime}~${event.endTime}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
